<template>
  <!-- eslint-disable vue/no-deprecated-v-bind-sync -->
  <div id="content">
    <div class="header col-md-12">
      <div class="header-inner row align-items-center">
        <div class="col-md-4 left">
          <div class="title">
            {{ $t('sidebar.pos') }} > {{ $t('sidebar.posproduct') }}
          </div>
        </div>
        <div class="col-md-8 right">
          <div class="action">
            <base-dropdown-filter-download
              :items="DOWNLOAD_EXTS"
              :loading="false"
              @onSelect="handleSelectDownload"
            />
            <base-filter-data-type
              :type="dataType"
              :items="REPORT_DATA_TYPES"
              @onSelect="handleSelectDataType"
            />
            <base-dropdown-filter-date-range
              :type="dateType"
              :range="{ start: dateStart, end: dateEnd }"
              :active="isAppliedFilter"
              @onSubmit="handleSubmitFilter"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="content col-md-12">

      <p v-if="!ready">{{ $t('message.loading') }}</p>

      <div
        v-else
        class="col-md-12 no-padding mt-2">
        <template v-if="posProducts.length">
          <section class="mt-2 mb-4">
            <div>
              <section-title
                :title="datePeriod(dateStart, dateEnd)"
                fontweight="bold"/>
            </div>
          </section>
          <keep-alive>
            <ReportPosProductTable
              v-if="dataType === REPORT_DATA_TYPE_VALUE.TABLE"
              :items="posProducts"
              :columnNames="columnNames"
              :pickKeys="pickKeys"
              @onGetDetails="onGetDetails"
            />
            <div v-else>
              <div class="row mb-4">
                <div
                  class="col-2 d-flex justify-content-center d-flex align-items-center m-auto"
                  style="min-width: 267px;">
                  <select
                    v-model="selectedKey"
                    class="form-control custom-select">
                    <option
                      v-for="(key, index) in keys"
                      :key="index"
                      :value="key"
                    >
                      {{ getTextByKey(key) }}
                    </option>
                  </select>
                </div>
              </div>
              <report-pos-sku-chart
                v-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_BAR"
                :items="posProducts"
                :selectedKey="selectedKey"
                type="bar"
              />
              <report-pos-sku-chart
                v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_LINE"
                :items="posProducts"
                :selectedKey="selectedKey"
                type="line"
              />
              <report-pos-sku-chart
                v-else-if="dataType === REPORT_DATA_TYPE_VALUE.CHART_PIE"
                :items="posProducts"
                :selectedKey="selectedKey"
                type="pie"
              />
            </div>
          </keep-alive>
        </template>
        <template v-else>
          {{ $t('message.nodatafound') }}
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import { FILTER_DATE_TYPE, DOWNLOAD_EXTS, DOWNLOAD_EXT_VALUE, REPORT_DATA_TYPES, REPORT_DATA_TYPE_VALUE, REPORT_DATA_TYPE_ICON, REPORT_DATA_TYPE_TEXT } from '../../../../helpers/enum'
import { getDateStart, getDateEnd, getLastSevenDay } from '../../../../helpers'
import sidebarModule from './../../sidebarModule'
import XLSX from 'xlsx'
import { getFileNamePosSku } from '@/helpers/exportFormater'
import ReportPosProductTable from '../../../../components/combine/ReportPosProductTable'
import userPermissionMixin from '@/mixins/userPermission'

const LAST_SEVEN_DAY = getLastSevenDay()

export default {
  mixins: [userPermissionMixin],

  components: {
    ReportPosProductTable,
  },

  data () {
    return {
      REPORT_DATA_TYPES: [
        ...REPORT_DATA_TYPES,
        {
          icon: REPORT_DATA_TYPE_ICON.CHART_PIE,
          text: REPORT_DATA_TYPE_TEXT.CHART_PIE,
          value: REPORT_DATA_TYPE_VALUE.CHART_PIE,
        },
      ],
      REPORT_DATA_TYPE_VALUE,
      DOWNLOAD_EXTS,
      ready: false,
      dateType: FILTER_DATE_TYPE.DAY,
      dateStart: LAST_SEVEN_DAY.START,
      dateEnd: LAST_SEVEN_DAY.END,
      dataType: REPORT_DATA_TYPE_VALUE.TABLE,
      chartSelectedLabels: {},
      isAppliedFilter: false,
      selectedKey: 'quantity',
    }
  },

  computed: {
    ...mapGetters({
      warehouses: 'getWarehouses',
      dateformat: 'getDateFormat',
      products: 'getProducts',
      getProductFullNameBySkuId: 'getProductFullNameBySkuId',
    }),
    posProducts () {
      return this.$store.state.report.posProducts.map((item) => {
        return {
          ...item,
          date: (() => {
            if (this.dateType === FILTER_DATE_TYPE.DAY) {
              return this.getMyLocalDate(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.MONTH) {
              return this.getMyLocalMonthYear(item.date, { short: false })
            } else if (this.dateType === FILTER_DATE_TYPE.YEAR) {
              return this.getMyLocalYear(item.date, { short: false })
            }
            return item.date
          })(),
          customer: item.skus.reduce((acc, cur) => acc + cur.customer, 0),
          quantity: item.skus.reduce((acc, cur) => acc + cur.quantity, 0),
          cost: item.skus.reduce((acc, cur) => acc + cur.cost, 0),
          discount: item.skus.reduce((acc, cur) => acc + cur.discount, 0),
          tax: item.skus.reduce((acc, cur) => acc + cur.tax, 0),
          collect: item.skus.reduce((acc, cur) => acc + cur.collect, 0),
          refund: item.skus.reduce((acc, cur) => acc + cur.refund, 0),
          net: item.skus.reduce((acc, cur) => acc + cur.net, 0),
          profit: item.skus.reduce((acc, cur) => acc + cur.profit, 0),
          skus: item.skus.map((sku) => {
            const productName = this.getProductFullNameBySkuId(sku.skuId) || this.$t('apps.reports.message.unspecifiedproductname')
            const skuId = sku.skuId
            return {
              ...sku,
              info: {
                productName,
                skuId,
              },
            }
          }),
        }
      })
    },
    columnNames() {
      const items = [
        {
          style: { 'width': '140px', 'max-width': '140px' },
          text: this.$t('apps.reports.message.customer'),
        },
        {
          style: { 'width': '140px' },
          text: this.$t('apps.reports.message.quantity'),
        },
      ]
      if (this.getRoleMetadata.view.fields.cost) {
        items.push({
          style: { 'width': '140px' },
          text: this.$t('apps.reports.message.cost'),
        })
      }
      if (this.getRoleMetadata.view.fields.discount) {
        items.push({
          style: { 'width': '100px' },
          text: this.$t('apps.reports.message.discount'),
        })
      }
      if (this.getRoleMetadata.view.fields.tax) {
        items.push({
          style: { 'width': '100px' },
          text: this.$t('apps.reports.message.tax'),
        })
      }
      if (this.getRoleMetadata.view.fields.collect) {
        items.push({
          style: { 'width': '140px' },
          text: this.$t('apps.reports.message.collect'),
        })
      }
      if (this.getRoleMetadata.view.fields.refund) {
        items.push({
          style: { 'width': '100px' },
          text: this.$t('apps.reports.message.refund'),
        })
      }
      if (this.getRoleMetadata.view.fields.net) {
        items.push({
          style: { 'width': '140px' },
          text: this.$t('apps.reports.message.net'),
        })
      }
      if (this.getRoleMetadata.view.fields.profit) {
        items.push({
          style: { 'width': '140px' },
          text: this.$t('apps.reports.message.profit'),
        })
      }
      return items
    },
    pickKeys() {
      return [
        'customer',
        'quantity',
        this.getRoleMetadata.view.fields.cost ? 'cost' : null,
        this.getRoleMetadata.view.fields.discount ? 'discount' : null,
        this.getRoleMetadata.view.fields.tax ? 'tax' : null,
        this.getRoleMetadata.view.fields.collect ? 'collect' : null,
        this.getRoleMetadata.view.fields.refund ? 'refund' : null,
        this.getRoleMetadata.view.fields.net ? 'net' : null,
        this.getRoleMetadata.view.fields.profit ? 'profit' : null,
      ].filter((v) => v)
    },
    keys() {
      return [
        'customer',
        'quantity',
        this.getRoleMetadata.view.fields.cost ? 'cost' : null,
        this.getRoleMetadata.view.fields.discount ? 'discount' : null,
        this.getRoleMetadata.view.fields.tax ? 'tax' : null,
        this.getRoleMetadata.view.fields.collect ? 'collect' : null,
        this.getRoleMetadata.view.fields.refund ? 'refund' : null,
        this.getRoleMetadata.view.fields.net ? 'net' : null,
        this.getRoleMetadata.view.fields.profit ? 'profit' : null,
      ].filter((v) => v)
    },
  },

  beforeMount () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.reports.title'),
      current: {
        app_name: 'reports',
        app_id: 8,
        module_id: 2,
        submodule_id: 3,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 1,
      submenuAtIdx: 2,
    })
  },

  async created () {
    if (this.products.length == 0) {
      await this.$store.dispatch('getProducts')
    }
    await this.fetchData()
  },

  destroyed: function () {
    this.leavePage(this.$Progress, [])
  },

  methods: {
    async fetchData () {
      this.ready = false
      await this.$store.dispatch('getReportPosProducts', {
        'filter[date_start]': getDateStart(this.dateStart),
        'filter[date_end]': getDateEnd(this.dateEnd),
        'filter[date_type]': this.dateType,
        'timezone': moment.tz.guess(),
      })
      this.ready = true
      this.showFinish(this.$Progress)
    },
    handleSubmitFilter ({ type, start, end }) {
      this.dateType = type
      this.dateStart = start
      this.dateEnd = end
      this.isAppliedFilter = true
      this.fetchData()
    },
    handleSelectDataType ({ value = null }) {
      this.dataType = value
    },
    handleSelectDownload ({ value = null }) {
      if (value === DOWNLOAD_EXT_VALUE.EXCEL) {
        this.exportAs('xlsx')
      } else if (value === DOWNLOAD_EXT_VALUE.CSV) {
        this.exportAs('csv')
      }
    },
    async exportAs (fileType) {
      /* convert state to workbook */
      const headColumns = [
        this.$t('message.date'),
        this.$t('message.item'),
        this.$t('apps.reports.message.customer'),
        this.$t('apps.reports.message.quantity'),
        this.getRoleMetadata.view.fields.cost ? this.$t('message.cost') : '{hiden}',
        this.getRoleMetadata.view.fields.discount ? this.$t('message.discount') : '{hiden}',
        this.getRoleMetadata.view.fields.tax ? this.$t('message.tax') : '{hiden}',
        this.getRoleMetadata.view.fields.collect ? this.$t('message.collect') : '{hiden}',
        this.getRoleMetadata.view.fields.refund ? this.$t('message.refund') : '{hiden}',
        this.getRoleMetadata.view.fields.net ? this.$t('message.net') : '{hiden}',
        this.getRoleMetadata.view.fields.profit ? this.$t('message.profit') : '{hiden}',
      ].filter((v) => v !== '{hiden}')
      const bodyRows = this.$store.state.report.posProducts.reduce((acc, cur) => {
        const results = cur.skus.map((skuItem) => (
          [
            cur.date,
            this.getProductFullNameBySkuId(skuItem.skuId) || this.$t('apps.reports.message.unspecifiedproductname'),
            skuItem.customer,
            skuItem.quantity,
            this.getRoleMetadata.view.fields.cost ? skuItem.cost : '{hiden}',
            this.getRoleMetadata.view.fields.discount ? skuItem.discount : '{hiden}',
            this.getRoleMetadata.view.fields.tax ? skuItem.tax : '{hiden}',
            this.getRoleMetadata.view.fields.collect ? skuItem.collect : '{hiden}',
            this.getRoleMetadata.view.fields.refund ? skuItem.refund : '{hiden}',
            this.getRoleMetadata.view.fields.net ? skuItem.net : '{hiden}',
            this.getRoleMetadata.view.fields.profit ? skuItem.profit : '{hiden}',
          ].filter((v) => v !== '{hiden}')
        ))
        acc = [
          ...acc,
          ...results,
        ]
        return acc
      }, [])
      const data = [headColumns, ...bodyRows]
      const ws = XLSX.utils.aoa_to_sheet(data)
      const wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet')

      /* generate file and send to client */
      const fileName = getFileNamePosSku(this.$t, fileType)

      XLSX.writeFile(wb, fileName)
    },
    getTextByKey (key) {
      const patt = {
        'transaction': this.$t('message.transactions'),
        'customer': this.$t('apps.reports.message.customer'),
        'quantity': this.$t('apps.reports.message.quantity'),
        'cost': this.$t('apps.reports.message.cost'),
        'discount': this.$t('apps.reports.message.discount'),
        'tax': this.$t('apps.reports.message.tax'),
        'collect': this.$t('apps.reports.message.collect'),
        'refund': this.$t('apps.reports.message.refund'),
        'net': this.$t('apps.reports.message.net'),
        'profit': this.$t('apps.reports.message.profit'),
      }
      return patt[key]
    },
    async onGetDetails(skuId, callback) {
      await this.$store.dispatch('getReportPosProductsDetails', {
        'filter[date_start]': getDateStart(this.dateStart),
        'filter[date_end]': getDateEnd(this.dateEnd),
        'filter[date_type]': this.dateType,
        'timezone': moment.tz.guess(),
        'sku_id': skuId,
      })
      callback(this.$store.state.report.posProductsDetails)
      this.showFinish(this.$Progress)
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/apps/report/pos/Product.vue